import { useParams, useSearchParams } from "react-router-dom"
import { Button } from "react-bootstrap"
import { useEffect, useState } from "react"

import { useFetchClubQuery } from "../../app/clubsApiSlice"
import Spinner from "../../components/Spinner"
import DisplayErrorMessage from "../../components/DisplayErrorMessage"
import ViewClub from "./ViewClub"
import ViewClubMembers from "./ViewClubMembers"
import ClubNotifications from "./ClubNotifications"
import useAuth from "../../hooks/useAuth"
import BackButtonHeader from "../../components/buttons/BackButtonHeader"
import formatClubName from "../../utils/formatClubName"

const ViewClubPage = () => {
  const { role, uid } = useAuth()
  const { id: clubId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: club, isLoading, isSuccess, isError, error } = useFetchClubQuery({ clubId, uid })

  const [currentPage, setCurrentPage] = useState("overview")

  // Update currentPage from URL search params
  useEffect(() => {
    const urlCurrentPage = searchParams.get("currentPage")

    if (urlCurrentPage) {
      setCurrentPage(urlCurrentPage)
    }
  }, [])

  // Update URL search params when currentPage changes
  useEffect(() => {
    const newParams = new URLSearchParams()
    
    newParams.set("currentPage", String(currentPage))

    setSearchParams(newParams, { replace: true })
  }, [currentPage, setSearchParams])

  if (isLoading) return <Spinner />
  if (isError) {
    console.error(error || "Error retrieving club")
    return <DisplayErrorMessage>Error retrieving club data</DisplayErrorMessage>
  }
  if (!isSuccess || !club) return null

  const isSecretary = club.isClubSecretary

  let pageContent

  switch (currentPage) {
    case "overview":
      pageContent = <ViewClub clubId={clubId} club={club} />
      break
    case "members":
      pageContent = <ViewClubMembers club={club} clubId={clubId} />
      break
    case "notifications":
      pageContent = <ClubNotifications clubId={club.clubId} clubName={club.clubName} notifications={club.notifications} />
      break
    default:
      pageContent = <ViewClub club={club} />
      break
  }

  return (
    <div className="m-0 p-0 w-100">
      <BackButtonHeader page={formatClubName(club.clubName)} />

      {/* Only show buttons if user is admin or secretary */}
      {(role === "admin" || isSecretary) && (
        <div className="d-flex flex-wrap gap-3 mb-2">
          <Button
            variant=""
            className={currentPage === "overview"
              ? "border-0 rounded-3 shadow-sm bg-white fw-semibold"
              : "border-0 rounded-3 shadow-sm fw-semibold text-muted"
            } onClick={() => setCurrentPage("overview")}>
            Overview
          </Button>
          <Button
            variant=""
            className={currentPage === "members"
              ? "border-0 rounded-3 shadow-sm bg-white fw-semibold"
              : "border-0 rounded-3 shadow-sm fw-semibold text-muted"
            }
            onClick={() => setCurrentPage("members")}>
            Members
          </Button>

          {/* Only show notifications button if user is secretary */}
          {isSecretary &&
            <Button
              variant=""
              className={currentPage === "notifications"
                ? "border-0 position-relative shadow-sm bg-white fw-semibold"
                : "border-0 position-relative shadow-sm fw-semibold text-muted"
              }
              onClick={() => setCurrentPage("notifications")}>
              Notifications
              {club.notifications > 0 && <div className="club-notifications">{club.notifications}</div>}
            </Button>
          }
        </div>
      )}
      {pageContent}
    </div>
  )
}

export default ViewClubPage